"use client";

import { Logomark } from "@/components/logomark";
import { useSidebar } from "@/components/ui/sidebar";
import { type BreadcrumbDescriptor, useBreadcrumbs } from "@/hooks/use-breadcrumbs";
import { useWorkspaceNavigation } from "@/hooks/use-workspace-navigation";
import { routes } from "@/lib/navigation/routes";
import { IconMenu } from "@teral/icons";
import { cn } from "cn";
import Link from "next/link";
import { Fragment } from "react";
import { AccountCrumb } from "./account-crumb";
import { AppCrumb } from "./app-crumb";
import { CrumbSeparator } from "./crumb";
import { TopNavFeedbackButton } from "./feedback-button";
import { HelpButton } from "./help-button";
import { ProjectCrumb } from "./project-crumb";
import { UserButton } from "./user-button";
import { WorkspaceCrumb } from "./workspace-crumb";

export const TOP_NAV_HEIGHT = 52;

export function TopNav({ className }: { className?: string }) {
  const workspace = useWorkspaceNavigation();
  const crumbs = useBreadcrumbs();
  const { setOpenMobile } = useSidebar();

  return (
    <header
      className={cn("flex w-full shrink-0 items-center gap-1 bg-sunken pr-3 pl-3.5", className)}
      style={{ height: TOP_NAV_HEIGHT }}
    >
      <Link
        href={routes.workspaces.overview({ workspaceSlug: workspace.slug })}
        aria-label="Teral"
        className="mr-1 inline-flex items-center rounded-lg outline-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--focus-ring)"
      >
        <Logomark />
      </Link>
      <div className="flex min-w-0 items-center gap-1">
        <CrumbSeparator />
        {crumbs.map((descriptor, i) => (
          <Fragment key={crumbKey(descriptor)}>
            {i > 0 && <CrumbSeparator />}
            <CrumbForDescriptor descriptor={descriptor} />
          </Fragment>
        ))}
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-1">
        <TopNavFeedbackButton className="hidden md:flex" />
        <HelpButton />
        <UserButton />
        <button
          type="button"
          onClick={() => setOpenMobile(true)}
          aria-label="Open navigation"
          className="flex size-8 items-center justify-center rounded-md text-gray-10 hover:bg-grayA-3 hover:text-gray-12 md:hidden"
        >
          <IconMenu className="size-4" />
        </button>
      </div>
    </header>
  );
}

export function CrumbForDescriptor({ descriptor }: { descriptor: BreadcrumbDescriptor }) {
  switch (descriptor.type) {
    case "workspace":
      return <WorkspaceCrumb href={descriptor.href} />;
    case "account":
      return <AccountCrumb />;
    case "project":
      return <ProjectCrumb owner={descriptor.owner} />;
    case "app":
      return <AppCrumb projectId={descriptor.projectId} appId={descriptor.appId} />;
  }
}

export function crumbKey(descriptor: BreadcrumbDescriptor): string {
  switch (descriptor.type) {
    case "workspace":
      return "workspace";
    case "account":
      return "account";
    // One project crumb at most, and a stable key keeps it mounted while the
    // owning-project lookup resolves.
    case "project":
      return "project";
    case "app":
      return `app:${descriptor.appId}`;
  }
}
